import { Module } from '../Module';
import { IRequest } from '../ServerApi/types/IRequest';
import { ErrorReason, ServerService } from './ServerService';

export interface IPoller<T> {
  promise: ng.IPromise<T>;
  cancel: () => void;
}

export class PollService {

  public static $inject = ['ServerService', '$q', '$interval'];

  private static MaxPollPeriodMS = 3000;
  private static InitialPollPeriodMS = 300;
  private static PollPeriodMultiplier = 1.3;

  constructor(private server: ServerService,
              private $q: ng.IQService,
              private $interval: ng.IIntervalService) {}


  /**
   * Poll the request until isComplete callback returns true.
   *
   * Clients of poll() are responsible for calling poller.cancel() to prevent zombie polls
   * from hanging around. For example, if called directly from a controller the poller
   * should be canceled on $scope destruction:
   *
   *     $scope.$on('$destroy', () => {
   *       poller.cancel()
   *     });
   */
  public poll<TParams, TResult>(request: IRequest<TParams, TResult>,
                                isComplete: (response: TResult) => boolean,
                                customPollPeriod?: number): IPoller<TResult> {
    const deferred: ng.IDeferred<TResult> = this.$q.defer();

    const pollFulfilledFn = (result: TResult) => {
      if (isComplete(result)) {
        deferred.resolve(result);
      } else {
        deferred.notify(result);
        pollPeriod = (customPollPeriod) ? customPollPeriod : Math.min(PollService.PollPeriodMultiplier * pollPeriod,
          PollService.MaxPollPeriodMS);
        timeoutPromise = this.$interval(poll, pollPeriod, 1);
      }
    };

    const pollRejectedFn = (reason: ErrorReason) => {
      deferred.reject(reason);
    };

    const poll = () => {
      this.server.sendRequest(request).then(pollFulfilledFn, pollRejectedFn);
    };

    let pollPeriod = PollService.InitialPollPeriodMS;
    let timeoutPromise = this.$interval(poll, pollPeriod, 1);
    deferred.promise.catch(err => err);
    return {
      promise : deferred.promise,
      cancel: () => {
        deferred.reject(ErrorReason.Canceled);
        this.$interval.cancel(timeoutPromise);
      }
    };
  }

}

Module.instance().service('PollService', PollService);
